import { createSignal } from "solid-js";
import type { Todo } from "@listto/powersync";

import { closestSelector } from "@/lib/gestures/guards";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { TODO_ROW_SWIPE } from "@/lib/gestures/presets";

interface SwipeGesturesOptions {
  todo: () => Todo;
  isCompleted: () => boolean;
  isSelectionMode: () => boolean;
  onToggle: (todo: Todo) => void;
  onDelete: (id: string) => void;
  getSurfaceRef: () => HTMLDivElement | undefined;
  setOptimisticCompleted: (id: string, value: boolean | undefined) => void;
  optimisticTimeoutMs: number;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = TODO_ROW_SWIPE;

export function useTodoSwipeGestures(options: SwipeGesturesOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    canStart: ({ target }) => {
      if (options.isSelectionMode()) {
        return false;
      }
      // Ignore drags that begin on interactive controls or the drag handle.
      return !closestSelector(target, 'button, a, [data-no-todo-swipe="true"]');
    },
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setTx(applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: () => {
      hasSwiped = true;
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      const surfaceRef = options.getSurfaceRef();
      const width = surfaceRef?.clientWidth ?? FALLBACK_WIDTH_PX;
      const currentTodo = options.todo();

      if (finalTx > COMMIT_THRESHOLD_PX) {
        // Swipe right: complete / reactivate.
        setTx(width);
        const nextVal = !options.isCompleted();
        options.setOptimisticCompleted(currentTodo.id, nextVal);
        const targetId = currentTodo.id;
        setTimeout(() => {
          options.setOptimisticCompleted(targetId, undefined);
        }, options.optimisticTimeoutMs);
        setTimeout(() => {
          options.onToggle(currentTodo);
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
      } else if (finalTx < -COMMIT_THRESHOLD_PX) {
        // Swipe left: delete.
        setTx(-width);
        setTimeout(() => {
          options.onDelete(currentTodo.id);
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
      } else {
        // Cancel and bounce back to center.
        setTx(0);
        setTimeout(() => {
          setIsAnimating(false);
        }, CANCEL_ANIMATION_MS);
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, CANCEL_ANIMATION_MS);
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
    COMMIT_THRESHOLD_PX,
  };
}
